import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CartProvider } from './context/CartContext';
import AnalyticsTracker from './context/AnalyticsTracker';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppFloat from './components/WhatsAppFloat';

import Home        from './pages/Home';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import Cart        from './pages/Cart';
import Checkout    from './pages/Checkout';
import AboutUs     from './pages/AboutUs';
import FAQs        from './pages/FAQs';
import Blog, { BlogDetail } from './pages/Blog';
import Policies    from './pages/Policies';
import ContactUs   from './pages/ContactUs';
import Login       from './pages/Login';
import VerifyOtp   from './pages/VerifyOtp';
import Account from './pages/Account';
import CustomerOrderDetail from './pages/CustomerOrderDetail';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, refetchOnWindowFocus: false, staleTime: 1000 * 60 * 5 } },
});

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const NotFound = () => (
  <div className="container" style={{ textAlign: 'center', padding: '120px 24px' }}>
    <div style={{ fontSize: 72, marginBottom: 20 }}>🎁</div>
    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', marginBottom: 12 }}>Page Not Found</h2>
    <p style={{ color: 'var(--text-muted)', marginBottom: 28 }}>The page you're looking for doesn't exist.</p>
    <a href="/" className="btn btn-primary-warm btn-lg">Go Home</a>
  </div>
);

/* Pages that should NOT show Navbar/Footer */
const AUTH_PATHS = ['/login', '/verify-otp'];

function AppShell() {
  const { pathname } = useLocation();
  const isAuth = AUTH_PATHS.includes(pathname);
  const isProductDetail = /^\/products\/[^/]+$/.test(pathname);

  return (
    <div className="app-wrapper">
      {!isAuth && <Navbar />}
      <main className="app-main">
        <Routes>
          <Route path="/"              element={<Home />} />
          <Route path="/products"      element={<ProductList />} />
          <Route path="/products/:id"  element={<ProductDetail />} />
          <Route path="/cart"          element={<Cart />} />
          <Route path="/checkout"      element={<Checkout />} />
          <Route path="/about"         element={<AboutUs />} />
          <Route path="/faqs"          element={<FAQs />} />
          <Route path="/blogs"         element={<Blog />} />
          <Route path="/blogs/:slug"   element={<BlogDetail />} />
          <Route path="/policies"      element={<Policies />} />
          <Route path="/contact"       element={<ContactUs />} />
          <Route path="/login"         element={<Login />} />
          <Route path="/verify-otp"    element={<VerifyOtp />} />
          <Route path="/account" element={<Account />} />
          <Route path="/my-orders/:id" element={<CustomerOrderDetail />} />
          <Route path="*"              element={<NotFound />} />
        </Routes>
      </main>
      {!isAuth && <Footer />}
      {!isAuth && !isProductDetail && <WhatsAppFloat />}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <AnalyticsTracker />
          <AppShell />
        </Router>
      </CartProvider>
    </QueryClientProvider>
  );
}

export default App;